// utils/api.js - Complete API callers for all Firebase functions

const FIREBASE_FUNCTIONS_URL = 'https://elo-board.com/api';
// Validate admin access with listID and password
export const validateAccess = async (listID, password) => {
  try {
    
    const response = await fetch(`${FIREBASE_FUNCTIONS_URL}/validateAccess`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        listID,
        password
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to validate access');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error validating access:', error);
    throw error;
  }
};
//Update ELO ratings after a comparison
export const updateRanking = async (winner, loser, listID) => {
  try {
    const response = await fetch(`${FIREBASE_FUNCTIONS_URL}/updateRanking`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        winner,
        loser,
        listID
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update ranking');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error updating ranking:', error);
    throw error;
  }
};

//Get a items by ID
export const getItems = async (itemIds) => {
  try {
    const response = await fetch(`${FIREBASE_FUNCTIONS_URL}/getItems`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ itemIds })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to get items');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error getting items:', error);
    throw error;
  }
};

//Get all items for a list code
export const getAllItems = async (code, fingerprint) => {
  try {
    const response = await fetch(`${FIREBASE_FUNCTIONS_URL}/getAllItems?code=${encodeURIComponent(code)}&fingerprint=${encodeURIComponent(fingerprint)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to get items');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error getting all items:', error);
    throw error;
  }
};

// Get list data (alias for getAllItems for consistency with AdminPanel)
export const getList = async (listId) => {
  try {
    const data = await getAllItems(listId);
    
    // Transform the data to match the expected format in AdminPanel
    const items = Object.entries(data.items || {}).map(([itemId, itemData]) => ({
      id: itemId,
      name: itemData.name,
      imageUrl: itemData.image_url || itemData.imageUrl || '',
      rating: itemData.elo || 1200,
      wins: itemData.wins || 0,
      losses: itemData.losses || 0
    }));

    return {
      id: listId,
      title: data.meta?.name || 'Untitled List',
      items: items,
      meta: data.meta
    };
  } catch (error) {
    console.error('Error getting list:', error);
    throw error;
  }
};

//Add a new item to a list
export const addItem = async (name, imageUrl, listID, password) => {
  try {
    const queryParams = new URLSearchParams({
      name,
      image_url: imageUrl || '',
      listID,
      password
    });

    const response = await fetch(`${FIREBASE_FUNCTIONS_URL}/addItem?${queryParams}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to add item');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error adding item:', error);
    throw error;
  }
};

// Remove an item from a list
export const removeItem = async (itemID, listID, password) => {
  try {
    const queryParams = new URLSearchParams({
      itemID,
      listID,
      password
    });

    const response = await fetch(`${FIREBASE_FUNCTIONS_URL}/removeItem?${queryParams}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to remove item');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error removing item:', error);
    throw error;
  }
};

// Create a new list with items
export const createNewList = async (title, description, items, creatorId) => {
  try {
    console.log(`${FIREBASE_FUNCTIONS_URL}/createNewList`, JSON.stringify({title,items,creatorId}))
    const response = await fetch(`${FIREBASE_FUNCTIONS_URL}/createNewList`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        title,
        description,
        items,
        creatorId
      })
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create list');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error creating new list:', error);
    throw error;
  }
};

// Update multiple items in a list (for admin panel)
export const updateList = async (listId, data) => {
  try {
    const response = await fetch(`${FIREBASE_FUNCTIONS_URL}/updateListItems`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        listID: listId,
        items: data.items,
        adminCode: data._adminCode
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update list');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error updating list:', error);
    throw error;
  }
};

//Generate a unique ID (utility function)
export const generateId = () => {
  return Math.random().toString(36).substr(2, 9);
};

//Helper function to handle API errors consistently
export const handleApiError = (error) => {
  if (error.message.includes('Invalid credentials')) {
    return 'Invalid access code. Please check your credentials.';
  } else if (error.message.includes('not found')) {
    return 'The requested resource was not found.';
  } else if (error.message.includes('Method not allowed')) {
    return 'Invalid request method.';
  } else {
    return error.message || 'An unexpected error occurred.';
  }
};

// Export configuration for easy access
export const FIREBASE_CONFIG = {
  FUNCTIONS_URL: FIREBASE_FUNCTIONS_URL
};

// Base64 client upload function - avoids multipart form issues
export const uploadImage = async (file) => {
  // Validate file on client side
  if (!file) {
    throw new Error('No file provided');
  }

  if (!file.type.startsWith('image/')) {
    throw new Error('Only image files are allowed');
  }

  if (file.size > 5 * 1024 * 1024) {
    throw new Error('File too large. Maximum size is 5MB');
  }

  if (file.size === 0) {
    throw new Error('File is empty');
  }

  console.log('Converting file to base64:', {
    name: file.name,
    size: file.size,
    type: file.type
  });

  try {
    // Convert file to base64
    const base64Data = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        resolve(e.target.result);
      };
      
      reader.onerror = (e) => {
        reject(new Error('Failed to read file'));
      };
      
      reader.onabort = (e) => {
        reject(new Error('File reading was aborted'));
      };
      
      reader.readAsDataURL(file);
    });

    console.log('File converted to base64, length:', base64Data.length);

    // Send as JSON instead of multipart form
    const response = await fetch(`${FIREBASE_FUNCTIONS_URL}/uploadImage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        imageData: base64Data,
        fileName: file.name,
        mimeType: file.type
      })
    });

    console.log('Upload response status:', response.status);

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        throw new Error(`Upload failed with status ${response.status}`);
      }
      throw new Error(errorData.error || `Upload failed with status ${response.status}`);
    }

    const result = await response.json();
    console.log('Upload successful:', result);
    return result;

  } catch (error) {
    console.error('Upload error:', error);
    
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    
    if (error.message.includes('Failed to read file')) {
      throw new Error('Could not read the selected file. Please try a different file.');
    }
    
    throw error;
  }
};

// Alternative fetch-based implementation with retry logic
// Alternative base64 JSON implementation with retry logic
export const uploadImageWithRetry = async (file, maxRetries = 3, onProgress = null) => {
  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Validate file
      if (!file) {
        throw new Error('No file provided');
      }

      if (!file.type.startsWith('image/')) {
        throw new Error('Only image files are allowed');
      }

      if (file.size > 5 * 1024 * 1024) {
        throw new Error('File too large. Maximum size is 5MB');
      }

      if (file.size === 0) {
        throw new Error('File is empty or corrupted');
      }

      console.log(`Upload attempt ${attempt}/${maxRetries} for file:`, file.name);

      // Convert file to base64
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.onabort = () => reject(new Error('File reading was aborted'));

        reader.readAsDataURL(file);
      });

      console.log(`Base64 conversion complete, length: ${base64Data.length}`);

      // Send request as JSON
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60s timeout

      const response = await fetch(`${FIREBASE_FUNCTIONS_URL}/uploadImage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          imageData: base64Data,
          fileName: file.name,
          mimeType: file.type
        })
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log(`Upload successful on attempt ${attempt}`);
      return result;

    } catch (error) {
      console.error(`Upload attempt ${attempt} failed:`, error);
      lastError = error;

      if (error.message.includes('too large') ||
          error.message.includes('Only image files') ||
          error.message.includes('No file provided') ||
          error.message.includes('empty or corrupted')) {
        throw error;
      }

      if (attempt === maxRetries) {
        throw error;
      }

      const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
      console.log(`Waiting ${delay}ms before retry...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError || new Error('Upload failed after all retry attempts');
};
